class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (!this.notification) return;
    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (!this.notification) return;
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (!this.notification) return;
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  // Touma fix (2026-10-05): the /cart/add.js response does not always carry
  // `sections` (it can come back null), which threw
  // "Cannot read properties of null (reading 'cart-notification-product')".
  // We now fall back to the Section Rendering API and never throw, so the
  // notification always opens and the cart bubble / count / subtotal refresh.
  async renderContents(parsedState) {
    this.cartItemKey = parsedState && parsedState.key;
    const sectionsToRender = this.getSectionsToRender();
    let sections = parsedState && parsedState.sections ? parsedState.sections : null;

    const missing = !sections || sectionsToRender.some((section) => !sections[section.id]);
    if (missing) {
      try {
        const ids = sectionsToRender.map((section) => section.id).join(',');
        const response = await fetch(`${window.location.pathname}?sections=${ids}`, {
          headers: { Accept: 'application/json' },
        });
        const fresh = await response.json();
        sections = Object.assign({}, fresh || {}, sections || {});
      } catch (e) {
        sections = sections || {};
      }
    }

    sectionsToRender.forEach((section) => {
      const mount = document.getElementById(section.id);
      const html = sections[section.id];
      if (!mount || !html) return;
      let inner = this.getSectionInnerHTML(html, section.selector);
      if (inner === null && section.selector) inner = this.getSectionInnerHTML(html);
      if (inner !== null) mount.innerHTML = inner;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: this.cartItemKey ? `[id="cart-notification-product-${this.cartItemKey}"]` : undefined,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-subtotal',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector || '.shopify-section');
    return element ? element.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
